import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { importQuizSteps, importQuizThanks } from './lazyPages';
import Service from './services';

// Mesma checagem sincrona (so localStorage, sem rede) que cada pagina ja faz
// sozinha no proprio componentDidMount - instanciada uma vez so aqui, fora
// do componente, pra nao recriar o objeto a cada render.
const authService = new Service();

// Lazy tambem: o Sidebar (Menu do antd, icones, imagens) so deve pesar
// no bundle de quem realmente entra na area logada - nao em quem so
// acessa /login ou o questionario publico.
const AppShell = lazy(() => import('./components/AppShell'));

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const CreatePassword = lazy(() => import('./pages/CreatePassword'));
const RecoverPassword = lazy(() => import('./pages/RecoverPassword'));
const Forgot = lazy(() => import('./pages/Login/Forgot'));
const Register = lazy(() => import('./pages/Register'));
const ListUsers = lazy(() => import('./pages/ListUsers'));
const ListContract = lazy(() => import('./pages/Contract'));
const ListCompany = lazy(() => import('./pages/Company'));
const CompanyClient = lazy(() => import('./pages/CompanyClient'));
const HomeClient = lazy(() => import('./pages/Home/HomeClient'));

const Comments = lazy(() => import('./pages/Comments'));
const Evolution = lazy(() => import('./pages/Evolution'));
const IndexBlue = lazy(() => import('./pages/IndexBlue'));
const Indicadores = lazy(() => import('./pages/Indicadores'));
const Nps = lazy(() => import('./pages/Nps'));
const SettingsBlue = lazy(() => import('./pages/SettingsBlue'));
// import ModalContracts from './components/ModalContracts';
const ListPlans = lazy(() => import('./pages/Plans'));
// Catalogo de perguntas (area admin) - ver plano perguntas-abertas-e-privadas.
const Questions = lazy(() => import('./pages/Questions'));
const RegisterCompany = lazy(() => import('./pages/RegisterCompany'));
const PlansChoice = lazy(() => import('./pages/PlansChoice'));
const Invitation = lazy(() => import('./pages/Invitation'));
const ReviewCompany = lazy(() => import('./pages/ReviewCompany'));

const Quiz = lazy(() => import('./pages/Quiz'));
// Reaproveitam o import() de lazyPages.js pra que a tela Quiz possa
// pre-carregar esses chunks sem o webpack duplicar o codigo - ver lazyPages.js.
const QuizSteps = lazy(importQuizSteps);
const QuizThanks = lazy(importQuizThanks);

const Main = lazy(() => import('./pages/Main'));

const routeLoadingFallback = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div className="app-spinner" />
  </div>
);

// AppShell (com o Sidebar) fica fora do Suspense de cada rota, entao so
// as paginas da area logada (abaixo) ficam dentro dele - as publicas
// (login, cadastro, questionario etc.) continuam sem sidebar, como sempre.
//
// Achado 2026-08-19: sem a checagem abaixo, visitar qualquer rota daqui
// (inclusive "/", a mais comum - ex.: entrar direto em
// app.blueexperiencias.com.br) montava o AppShell (Header+Sidebar)
// IMEDIATAMENTE, antes de qualquer pagina checar se a pessoa esta logada -
// so depois, no componentDidMount da pagina (ex.: Home), o isLogged()
// existente mandava pra /login. Isso causava uma "piscada" visivel de
// Header/Sidebar antes da tela de login aparecer pra quem nao esta logado.
// Corrigido aqui, no unico lugar por onde toda essa area passa - as
// checagens isLogged() que ja existem em cada pagina continuam la, sem
// mudanca (redundantes agora pra quem chega logado, mas inofensivas).
const AuthenticatedArea = () => {
  if (!authService.isLogged()) {
    return <Redirect to="/login" />;
  }

  return (
    <AppShell>
      <Switch>
        <Route path="/" exact component={Home} />
        <Route path="/client" exact component={HomeClient} />
        <Route path="/administrators" exact component={ListUsers} />
        <Route path="/companys" exact component={ListCompany} />
        <Route path="/contracts" exact component={ListContract} />
        <Route path="/plans" exact component={ListPlans} />
        <Route path="/questions" exact component={Questions} />
        <Route path="/plansChoice" exact component={PlansChoice} />
        <Route path="/companyClient" exact component={CompanyClient} />
        <Route path="/comments" exact component={Comments} />
        <Route path="/indexBlue" exact component={IndexBlue} />
        <Route path="/indicadores" exact component={Indicadores} />
        <Route path="/nps" exact component={Nps} />
        <Route path="/evolution" exact component={Evolution} />
        <Route path="/settings" exact component={SettingsBlue} />
      </Switch>
    </AppShell>
  );
};

const Routes = () => (
  <BrowserRouter>
    <RouteErrorBoundary>
      <Suspense fallback={routeLoadingFallback}>
        <Switch>
          <Route path="/main" exact component={Main} />
          <Route path="/blue/questionario/:idEmpresa/:idFilial" exact component={Quiz} />
          <Route path="/blue/questionario/:idEmpresa/:idFilial/steps" exact component={QuizSteps} />
          {/* <Route path="/blue/questionario/:idFilial" exact component={QuizSteps} /> */}
          <Route path="/blue/questionario/:idEmpresa/:idFilial/obrigado/:idBeneficio?" exact component={QuizThanks} />
          <Route path="/invitation" exact component={Invitation} />
          <Route path="/reviewCompany" exact component={ReviewCompany} />
          <Route path="/login" exact component={Login} />
          <Route path="/forgot" exact component={Forgot} />
          <Route path="/register" exact component={Register} />
          {/* <Route path="/register/:id/:guid" exact component={CreatePassword} /> */}
          <Route path="/createPassword/:id/:guid" exact component={CreatePassword} />
          <Route path="/createPassword" exact component={CreatePassword} />
          <Route path="/RecoverPassword" exact component={RecoverPassword} />
          <Route path="/RecoverPassword/:id/:guid" exact component={RecoverPassword} />
          <Route path="/registerCompany" exact component={RegisterCompany} />
          {/* <Route path="/modal" exact component={ModalContracts} /> */}
          <Route component={AuthenticatedArea} />
        </Switch>
      </Suspense>
    </RouteErrorBoundary>
  </BrowserRouter>
);

export default Routes;
